import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

interface ChartCardProps {
  title: string;
  type?: 'bar' | 'doughnut' | 'line';
  data?: any;
  fullWidth?: boolean;
  height?: number;
  titleColor?: string;
  subtitle?: string;
  icon?: any;
  children?: React.ReactNode;
}

const getColorValue = (color: string): string => {
  const colorMap: Record<string, string> = {
    green: '#10b981',
    blue: '#3b82f6',
    purple: '#a855f7',
    orange: '#f97316',
    red: '#ef4444',
    indigo: '#6366f1',
    pink: '#ec4899',
    yellow: '#eab308',
    cyan: '#06b6d4'
  }
  return colorMap[color] || '#3b82f6'
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  type,
  data,
  fullWidth = false,
  height = 300,
  titleColor,
  subtitle,
  icon: Icon,
  children
}) => {
  const chartRef = useRef<any>(null);

  // Configurações padrão para todos os gráficos
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: "'Inter', sans-serif"
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context: any) {
            const label = context.dataset.label || '';
            const value = context.parsed.y || context.parsed;
            
            if (type === 'doughnut') {
              return `${label}: ${value}`;
            }
            
            if (label.includes('R$')) {
              return `${label}: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
            }
            
            if (label.includes('km')) {
              return `${label}: ${value.toLocaleString('pt-BR')} km`;
            }
            
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: type !== 'doughnut' ? {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif"
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif"
          },
          callback: function(value: any) {
            if (title.includes('R$') || title.includes('Custo')) {
              return `R$ ${value.toLocaleString('pt-BR')}`;
            }
            if (title.includes('km') || title.includes('Distância')) {
              return `${value.toLocaleString('pt-BR')} km`;
            }
            return value;
          }
        }
      }
    } : undefined
  };

  // Renderizar gráfico baseado no tipo
  const renderChart = () => {
    if (!data || !data.labels || !data.datasets) {
      return (
        <div className="flex items-center justify-center h-full text-text-secondary">
          <div className="text-center">
            <div className="text-4xl mb-2">📊</div>
            <p>Dados não disponíveis</p>
          </div>
        </div>
      );
    }

    switch (type) {
      case 'bar':
        return (
          <Bar 
            ref={chartRef}
            data={data} 
            options={defaultOptions}
            height={height}
          />
        );
      
      case 'doughnut':
        return (
          <Doughnut 
            ref={chartRef}
            data={data} 
            options={{
              ...defaultOptions,
              cutout: '60%',
              plugins: {
                ...defaultOptions.plugins,
                legend: {
                  ...defaultOptions.plugins.legend,
                  position: 'bottom' as const
                }
              }
            }}
            height={height}
          />
        );
      
      case 'line':
        return (
          <Line 
            ref={chartRef}
            data={data} 
            options={{
              ...defaultOptions,
              plugins: {
                ...defaultOptions.plugins,
                legend: {
                  ...defaultOptions.plugins.legend,
                  position: 'top' as const
                }
              }
            }}
            height={height}
          />
        );
      
      default:
        return (
          <div className="flex items-center justify-center h-full text-text-secondary">
            <div className="text-center">
              <div className="text-4xl mb-2">❌</div>
              <p>Tipo de gráfico não suportado</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`bg-card rounded-xl overflow-hidden border border-border ${fullWidth ? 'col-span-full' : ''}`}>
      {titleColor ? (
        <div
          className="px-6 py-4 text-xl font-bold text-white"
          style={{
            background: `linear-gradient(135deg, ${getColorValue(titleColor)} 0%, ${getColorValue(titleColor)}dd 100%)`
          }}
        >
          {title}
        </div>
      ) : (
        <div className="px-6 pt-6">
          <h3 className="text-xl font-semibold text-text-primary mb-4 flex items-center">
            {Icon && <Icon className="h-5 w-5 mr-2" />}
            {title}
          </h3>
          {subtitle && <p className="text-sm text-text-secondary mb-4">{subtitle}</p>}
        </div>
      )}
      <div className={titleColor ? 'p-6' : ''} style={!children ? { height: `${height}px` } : {}}>
        {children ? children : renderChart()}
      </div>
    </div>
  );
};

export default ChartCard;
